import { Observable, Subscription } from 'rxjs';
import { readApiErrorStatus, readPage } from './alarms-api';

/**
 * Lista pobierana z serwera porcjami większymi niż to, co przyrasta na ekranie.
 *
 * Powód: każde żądanie do backendu kosztuje ~175 ms samego narzutu, zanim cokolwiek
 * policzy. Przy stronie po 10 wierszy każde kliknięcie paginacji było osobnym round
 * tripem. Pobieramy więc `batchSize` wierszy naraz i wydajemy z tej porcji kolejne
 * strony bez sieci — żądanie leci dopiero przy przekroczeniu granicy porcji.
 *
 * Ta sama lista obsługuje DWA sposoby wydawania wierszy, a wybiera go wywołujący:
 *
 * - **strona z porcji** (`chunkFor` + `setChunk` + `showPage`) — pod kontrolki paginacji;
 *   nowa porcja podmienia poprzednią, na ekranie stoi `pageSize` wierszy;
 * - **doklejanie** (`appendChunk`, lista tworzona przez `PagedList.infinite`) — pod
 *   dociąganie przy przewijaniu; porcje sumują się, a `nextOffset` i `hasMore` mówią,
 *   o co i czy w ogóle pytać dalej.
 *
 * Wspólne zostaje wszystko, co widzi szablon: `items`, `total`, `loading`, `error`.
 *
 * Klasa nie zna HTTP: mówi tylko, czego jej brakuje, i przyjmuje wynik. Dzięki temu
 * testuje się bez TestBed.
 */
export class PagedList<T = any> {

  /** Wiersze bieżącej strony — to renderuje szablon. */
  items: T[] = [];

  /** Liczba wszystkich wierszy na serwerze — zasila [total] komponentu paginacji. */
  total = 0;

  /**
   * Czy trwa pobieranie porcji. Stan trzymamy per sekcja, bo jedna wspólna flaga
   * na cały widok gasła po pierwszej odpowiedzi i pozostałe sekcje pokazywały
   * „brak danych", chociaż wciąż czekały na swoje.
   */
  loading = false;

  /**
   * Status nieudanego pobrania (np. 404, 400) albo null. Bez tego widok pokazywałby
   * przy awarii dokładnie to samo co przy pustej odpowiedzi — „brak danych" — i nie
   * dało się odróżnić „nic nie ma" od „nie udało się zapytać".
   */
  error: string = null;

  /**
   * Tryb doklejania: od którego wiersza zamówić następną porcję. Liczy wiersze
   * PRZYSŁANE, nie doklejone — odsiane duplikaty zajmują miejsce po stronie serwera,
   * więc pominięcie ich w przesunięciu kazałoby pytać w kółko o to samo.
   */
  nextOffset = 0;

  /** Tryb doklejania: `false` dopiero wtedy, gdy wiemy, że nie ma czego dociągać. */
  hasMore = true;

  private chunk: T[] = [];
  private chunkStart = -1;

  /**
   * Offset strony, którą właśnie widać. `showPage` i tak po nim tnie — pamiętamy go,
   * żeby dało się odpowiedzieć na pytanie „czy użytkownik patrzy na początek listy",
   * od którego zależy, czy nowe wiersze można dołożyć na wierzch, czy tylko zgłosić.
   */
  private pageOffset = 0;

  /**
   * Identyfikatory wierszy już doklejonych. Rośnie razem z listą, zamiast być składany
   * od nowa przy każdej porcji — inaczej doklejenie n-tej porcji przechodziłoby po
   * wszystkich dotychczasowych wierszach, i to w trakcie przewijania.
   */
  private seen = new Set<any>();

  constructor(public readonly pageSize: number, public readonly batchSize: number) {}

  /**
   * Lista dociągana przy przewijaniu. Strona i porcja to tu jedno i to samo, więc
   * `pageSize` nie ma czego znaczyć — fabryka istnieje po to, żeby nie zostawiać
   * w wywołaniu zagadkowego `new PagedList(50, 50)`.
   */
  static infinite<T = any>(fetchSize: number): PagedList<T> {
    return new PagedList<T>(fetchSize, fetchSize);
  }

  /**
   * Offset porcji, którą trzeba pobrać, żeby pokazać stronę zaczynającą się na
   * `offset`. `null`, gdy ta strona siedzi już w pamięci.
   */
  chunkFor(offset: number): number | null {
    const start = Math.floor(offset / this.batchSize) * this.batchSize;
    return start === this.chunkStart ? null : start;
  }

  /** Wydaje stronę z porcji już pobranej. Bez sieci. */
  showPage(offset: number): void {
    this.pageOffset = offset;

    if (this.chunkStart < 0) {
      this.items = [];
      return;
    }
    const from = offset - this.chunkStart;
    this.items = this.chunk.slice(from, from + this.pageSize);
  }

  /** Czy widać początek listy — tam i tylko tam nowe wiersze mogą wejść na wierzch. */
  get isFirstPage(): boolean {
    return this.pageOffset === 0;
  }

  /**
   * Unieważnia pobraną porcję, ZOSTAWIAJĄC to, co widać. Kolejne pytanie o tę samą
   * stronę pójdzie wtedy do serwera, zamiast wyjść z pamięci.
   *
   * Różnica wobec `reset()` jest widoczna dla użytkownika: reset czyści też `items`,
   * więc lista na moment staje się pusta i widok czyta to jako pierwsze ładowanie.
   * Tutaj chodzi o odświeżenie tego, co już stoi na ekranie.
   */
  invalidateChunk(): void {
    this.chunk = [];
    this.chunkStart = -1;
  }

  /** Znacznik startu pobierania — zapala wskaźnik ładowania i kasuje poprzedni błąd. */
  beginFetch(): void {
    this.loading = true;
    this.error = null;
  }

  /** Koniec pobierania bez wyniku — gasi wskaźnik, zapamiętuje status, zostawia to, co było. */
  failFetch(status?: any): void {
    this.loading = false;
    this.error = status ? String(status) : 'error';
  }

  /** Zapisuje pobraną porcję i od razu pokazuje z niej żądaną stronę. */
  setChunk(chunkStart: number, rows: T[], total: number, offset: number): void {
    this.chunkStart = chunkStart;
    this.chunk = rows || [];
    this.total = total;
    this.loading = false;
    this.error = null;
    this.showPage(offset);
  }

  /**
   * Wstawia świeże wiersze na GÓRĘ pierwszej porcji — alarm, który doszedł, gdy
   * użytkownik patrzy na początek listy. Serwer sortuje malejąco po dacie, więc nowe
   * należą na początek, a starsze mają się przesunąć w dół.
   *
   * Działa WYŁĄCZNIE na pierwszej porcji. Dalej porcja odpowiada wierszom serwera od
   * `chunkStart` w górę i wstawienie czegokolwiek na jej początek rozjechałoby ją
   * z offsetami — kolejne strony pokazywałyby nie to, co trzeba. Na dalszych stronach
   * wywołujący ma zamiast tego pokazać, że coś doszło.
   *
   * Porcję przycinamy do rozmiaru, w jakim ją pobrano: `showPage` tnie ją po
   * `offset - chunkStart`, więc porcja dłuższa niż `batchSize` wydawałaby na dalszych
   * stronach wiersze, których serwer pod tym offsetem nie ma.
   *
   * @param total liczba wszystkich wierszy prosto z odpowiedzi — bierzemy ją, zamiast
   *   dodawać wstawione do starej, bo w międzyczasie mogło dojść więcej, niż widać tutaj.
   * @returns ile wierszy naprawdę doszło; 0 znaczy „nic nowego" albo „nie ta porcja".
   */
  prependRows(rows: T[], total: number): number {
    if (this.chunkStart !== 0) {
      return 0;
    }

    // Wiersz bez identyfikatora przepuszczamy — nie ma po czym poznać, że to duplikat,
    // a zgubienie alarmu byłoby gorsze niż pokazanie go dwa razy.
    const fresh = updateKnownRows(this.chunk, rows);

    if (!fresh.length) {
      return 0;
    }

    this.chunk = fresh.concat(this.chunk).slice(0, this.batchSize);
    this.total = total;
    this.showPage(0);

    return fresh.length;
  }

  /**
   * Wierszy bez id nie dokładamy, bo odświeżanie co kilkanaście sekund dublowałoby je za każdym razem.
   * `nextOffset` zostaje, bo przesunięcie o dołożone przeskoczyłoby wiersze porcji, która już leci.
   */
  mergeHead(rows: T[], total: number): number {
    const fresh = updateKnownRows(this.items, rows).filter(row => identityOf(row) !== null);

    fresh.forEach(row => this.seen.add(identityOf(row)));
    this.items = fresh.concat(this.items);
    this.total = total;

    return fresh.length;
  }

  /**
   * Dokleja pobraną porcję do tego, co już stoi na ekranie.
   *
   * Wiersz przysłany drugi raz odsiewamy po `id`: listy raportów rosną w trakcie
   * czytania, a nowy wpis na górze przesuwa cały zbiór o jeden, więc ostatni wiersz
   * porcji wraca jako pierwszy wiersz następnej. Wiersze bez `id` idą bez odsiewu —
   * nie ma po czym porównywać, a zgubienie wiersza byłoby gorsze niż powtórzenie.
   *
   * @param hasMore `has_more` z koperty odpowiedzi.
   * @param sentCount ile wierszy PRZYSŁAŁ serwer. Bywa większe niż `rows.length`, gdy
   *   wywołujący odsiał wiersze nie do pokazania (np. bez identyfikatora) — przesunięcie
   *   musi liczyć przysłane, bo odsiane zajmują miejsce po stronie serwera i liczone
   *   z tego, co zostało, kazałoby pytać w kółko o tę samą porcję.
   */
  appendChunk(rows: T[], total: number, hasMore: boolean, sentCount?: number): void {
    const incoming = rows || [];
    const sent = sentCount === undefined ? incoming.length : sentCount;
    const fresh: T[] = [];

    incoming.forEach(row => {
      const id = identityOf(row);
      if (id !== null) {
        if (this.seen.has(id)) {
          return;
        }
        this.seen.add(id);
      }
      fresh.push(row);
    });

    this.nextOffset += sent;
    this.items = this.items.concat(fresh);
    this.total = total;
    this.loading = false;
    this.error = null;

    // Porcja bez ani jednego nowego wiersza znaczy, że serwer zaczął się powtarzać —
    // zignorował offset albo kolejność jest niestabilna. Bez tego warunku każde
    // zjechanie na dół powtarzałoby to samo żądanie w nieskończoność, bo `has_more`
    // dalej mówiłoby „jest więcej".
    this.hasMore = fresh.length > 0 && (hasMore || this.items.length < this.total);
  }

  /**
   * Wiersz skasowany na serwerze znika z listy bez pobierania jej od nowa.
   *
   * Cofnięcie `nextOffset` waży tu tyle samo co samo wycięcie: przesunięcie liczy wiersze
   * PRZYSŁANE, a serwer ma ich teraz o jeden mniej — bez tego następna porcja zaczęłaby się
   * o wiersz za daleko i ten, który wskoczył na zwolnione miejsce, nie pokazałby się nigdy.
   *
   * @returns czy wiersz był na widocznej liście
   */
  removeRow(row: T): boolean {
    if (this.items.indexOf(row) === -1) {
      return false;
    }

    const id = identityOf(row);
    if (id !== null) {
      this.seen.delete(id);
    }

    this.total = Math.max(0, this.total - 1);
    this.nextOffset = Math.max(0, this.nextOffset - 1);

    // Tryb doklejania nie ma porcji: `showPage` czytałoby z pustej i wyczyściło listę.
    if (this.chunkStart < 0) {
      this.items = this.items.filter(candidate => candidate !== row);
      return true;
    }

    this.chunk = this.chunk.filter(candidate => candidate !== row);
    this.showPage(this.pageOffset);

    return true;
  }
  /**
   * Czy trwa doklejanie kolejnej porcji — w odróżnieniu od pierwszego pobrania, które
   * zasłania ekran nakładką. Wskaźnik doklejania stoi pod listą i jej nie zakrywa.
   */
  get isAppending(): boolean {
    return this.loading && this.items.length > 0;
  }

  /**
   * Czy pokazywać licznik „X / Y". Ma sens tylko dopóki czegoś brakuje: przy komplecie
   * obie liczby są równe i wiersz mówi wyłącznie tyle, że nic więcej nie przyjdzie.
   */
  get showsProgress(): boolean {
    return this.items.length > 0 && this.items.length < this.total;
  }

  /**
   * Pierwsze pobranie — nie ma jeszcze czego pokazać, więc wskaźnik zajmuje miejsce
   * listy. Odwrotność `isAppending`: tam wiersze już są i doklejamy pod nimi.
   */
  get isInitialLoad(): boolean {
    return this.loading && !this.items.length;
  }

  /**
   * Pusty wynik, a nie awaria i nie trwające pobieranie. Rozróżnienie jest istotne:
   * „nic nie ma" kończy sprawę, a nieudane pobranie trzeba ponowić — pokazane tym
   * samym komunikatem znikłoby użytkownikowi z oczu.
   */
  get isEmpty(): boolean {
    return !this.loading && !this.error && !this.items.length;
  }

  /**
   * Porcja przestaje być aktualna — po zmianie filtra albo odświeżeniu listy.
   * Tryb doklejania zaczyna wtedy zbierać od nowa, więc wraca też przesunięcie
   * i zgoda na dociąganie.
   */
  reset(): void {
    this.chunk = [];
    this.chunkStart = -1;
    this.items = [];
    this.seen.clear();
    this.nextOffset = 0;
    this.pageOffset = 0;
    this.hasMore = true;
    this.loading = false;
  }
}

/** Klucz odsiewu duplikatów. `null`, gdy wiersz nie niesie identyfikatora. */
function identityOf(row: any): any {
  return row && row.id !== undefined && row.id !== null ? row.id : null;
}

/**
 * Znany wiersz dostaje nowe pola w miejscu, bo ta sama referencja zachowuje jego lokalny stan
 * (np. trwającą obsługę) i węzeł w DOM.
 */
function updateKnownRows<T>(known: T[], incoming: T[]): T[] {
  const byId = new Map(known.map(row => [identityOf(row), row] as [any, T]));
  const unknown: T[] = [];

  (incoming || []).forEach(row => {
    const id = identityOf(row);
    const match = id === null ? undefined : byId.get(id);
    if (match) {
      Object.assign(match, row);
    } else {
      unknown.push(row);
    }
  });

  return unknown;
}

export interface PagedChunkOptions<T> {
  /** Wiersz z API → wiersz, którym posługuje się szablon. Bez tego wiersze idą surowe. */
  mapRow?: (raw: any) => T;
  /**
   * Które wiersze w ogóle nadają się do pokazania — sprawdzane PO `mapRow`. Bez tego
   * wiersz, którego szablon nie umie wyrenderować (pusty, bez identyfikatora), leci
   * do widoku i wysypuje go dopiero na bindingu. Odsiew nie rusza przesunięcia:
   * `appendChunk` liczy je z wierszy przysłanych, nie zachowanych.
   */
  keepRow?: (row: T) => boolean;
  /**
   * Wywoływane po nieudanym żądaniu. Sama lista jest już oznaczona jako błędna —
   * to miejsce na komunikat dla użytkownika i wpis w konsoli. `status` jest wyłuskany
   * z koperty błędu, `error` zostaje surowy.
   */
  onError?: (status: string, error: any) => void;
  /**
   * Wywoływane po zapisaniu porcji, zanim wróci sterowanie. Istnieje dla stanu, który
   * żyje POZA listą — na przykład nakładki pierwszego wejścia gasnącej dopiero wtedy,
   * gdy rozstrzygnie się każde żądanie startowe widoku. Sama lista ma już wtedy
   * zgaszone `loading`, więc wołający czyta spójny stan.
   */
  onLoaded?: () => void;
}

/**
 * Jedna strona listy na ekranie: albo z pobranej już porcji (bez sieci), albo nowym
 * żądaniem po kolejną porcję.
 *
 * Powstało z trzech identycznych kopii tego samego ciągu kroków — sprawdź porcję,
 * zapal wskaźnik, rozpakuj kopertę, zmapuj wiersze, zapisz porcję, obsłuż błąd.
 * Każda kopia była okazją do pominięcia jednego z nich; najczęściej pomijanym była
 * łapka błędu, przez co awaria wyglądała w UI identycznie jak pusta lista.
 *
 * `PagedList` zostaje czystym stanem — to funkcja, a nie metoda, więc klasa dalej nie
 * wie nic o żądaniach i testuje się bez nich.
 *
 * @param request dostaje offset PORCJI (nie strony) i rozmiar porcji.
 * @returns subskrypcję żądania albo `null`, gdy strona znalazła się w pamięci i sieć
 *          nie była potrzebna. Wołający może ją ubić, gdy filtr zmieni się w locie.
 */
export function loadPagedChunk<T = any>(
  list: PagedList<T>,
  offset: number,
  request: (chunkStart: number, limit: number) => Observable<any>,
  options: PagedChunkOptions<T> = {}
): Subscription | null {

  const chunkStart = list.chunkFor(offset);

  if (chunkStart === null) {
    list.showPage(offset);
    return null;
  }

  list.beginFetch();

  return request(chunkStart, list.batchSize).subscribe(
    response => {
      const page = readPage(response, list.batchSize);
      const rows = options.mapRow ? page.items.map(item => options.mapRow(item)) : page.items as T[];
      list.setChunk(chunkStart, rows, page.total, offset);
      reportSuccess(options);
    },
    error => reportFailure(list, options, error)
  );
}

/**
 * Kolejna porcja listy dociąganej przy przewijaniu.
 *
 * Dwie strażnice na wejściu, obie po to, żeby wartownik przewijania nie zamawiał tego
 * samego dwa razy: nie pytamy, gdy poprzednie żądanie jeszcze nie wróciło (wartownik
 * potrafi wjechać w widok kilka razy pod rząd), i nie pytamy, gdy wiemy, że nie ma
 * czego dociągać. Trzymanie ich TUTAJ, a nie u wywołującego, jest tym, co pozwala
 * podpiąć obserwator wprost do tej funkcji bez powtarzania warunków przy każdej liście.
 *
 * @param request dostaje offset pierwszego brakującego wiersza i rozmiar porcji.
 * @returns subskrypcję żądania albo `null`, gdy żadne nie poleciało.
 */
export function loadNextChunk<T = any>(
  list: PagedList<T>,
  request: (offset: number, limit: number) => Observable<any>,
  options: PagedChunkOptions<T> = {}
): Subscription | null {

  if (list.loading || !list.hasMore) {
    return null;
  }

  list.beginFetch();

  return request(list.nextOffset, list.batchSize).subscribe(
    response => {
      const page = readPage(response, list.batchSize);
      const mapped = options.mapRow ? page.items.map(item => options.mapRow(item)) : page.items as T[];
      const rows = options.keepRow ? mapped.filter(row => options.keepRow(row)) : mapped;
      list.appendChunk(rows, page.total, page.hasMore, page.items.length);
      reportSuccess(options);
    },
    error => reportFailure(list, options, error)
  );
}

/** Porcja zapisana — stan poza listą może się do niej dostroić. Wspólne dla obu trybów. */
function reportSuccess<T>(options: PagedChunkOptions<T>): void {
  if (options.onLoaded) {
    options.onLoaded();
  }
}

/**
 * Nieudane żądanie oznacza listę i oddaje status wołającemu. Wspólne dla obu trybów,
 * bo to właśnie ten krok najłatwiej pominąć — a wtedy awaria wygląda w UI dokładnie
 * tak samo jak pusta lista.
 */
function reportFailure<T>(list: PagedList<T>, options: PagedChunkOptions<T>, error: any): void {
  const status = readApiErrorStatus(error);
  list.failFetch(status);
  if (options.onError) {
    options.onError(status, error);
  }
}
